import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Save, Trash2, Download, X, RefreshCw, ArrowLeft, Receipt,
  Repeat, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import SupplierPicker from "@/components/SupplierPicker";
import ExpenseBulkImportPanel from "@/components/ExpenseBulkImportPanel";

const FREQ_LABEL = { monthly: "Mensual", quarterly: "Trimestral", yearly: "Anual" };
const STATUS_LABEL = { pendiente: "Pendiente", pagado: "Pagado", conciliado: "Conciliado" };

function formatEUR(n) {
  return `€${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function firstOfMonth(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 10);
}
function lastOfMonth(offset = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return d.toISOString().slice(0, 10);
}

const PERIOD_PRESETS = [
  { id: "this_month", label: "Mes actual", get: () => [firstOfMonth(), lastOfMonth()] },
  { id: "last_month", label: "Mes anterior", get: () => [firstOfMonth(-1), lastOfMonth(-1)] },
  { id: "year", label: "Este año", get: () => [`${new Date().getFullYear()}-01-01`, todayISO()] },
  { id: "custom", label: "Personalizado", get: null },
];

export default function Expenses() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("admin", "billing");
  const canDelete = hasRole("admin");

  const [periodId, setPeriodId] = useState("this_month");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [supplierFilter, setSupplierFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);

  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({ base_amount: 0, vat_amount: 0, total: 0, count: 0, recurrent_active: 0 });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const pickPreset = (id) => {
    setPeriodId(id);
    const p = PERIOD_PRESETS.find((x) => x.id === id);
    if (p?.get) {
      const [f, t] = p.get();
      setFrom(f); setTo(t);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (supplierFilter.trim()) params.set("supplier", supplierFilter.trim());
      if (categoryFilter) params.set("category", categoryFilter);
      const [r, s, sett] = await Promise.all([
        api.get(`/expenses?${params.toString()}`),
        api.get("/expenses/summary/current-month"),
        api.get("/settings"),
      ]);
      setList(r.data);
      setSummary(s.data);
      setCategories(sett.data.expense_categories_catalog || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, supplierFilter, categoryFilter]);

  const onCreated = () => { setShowForm(false); setEditing(null); load(); };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este gasto? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Gasto eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runRecurrent = async () => {
    try {
      const r = await api.post("/expenses/run-recurrent");
      toast.success(`Se generaron ${r.data.generated} gastos recurrentes`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportXlsx = () => {
    const params = new URLSearchParams();
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    if (supplierFilter.trim()) params.set("supplier", supplierFilter.trim());
    if (categoryFilter) params.set("category", categoryFilter);
    fetch(`${api.defaults.baseURL}/expenses/export/xlsx?${params.toString()}`, fetchAuthOpts()).then(async (r) => {
      if (!r.ok) { toast.error("Error al exportar"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_gastos_${from || ""}_${to || ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1500px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 border-b border-dashed border-[#D5D7CE] pb-6">
        <div>
          <Link to="/billing" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[.14em] text-[#4B5158] hover:text-[#3F5222] font-semibold" data-testid="expenses-back">
            <ArrowLeft size={12} /> Facturación
          </Link>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">Gastos</h1>
          <p className="text-sm text-[#4B5158] mt-1">Registra y controla los gastos de la empresa. Marca recurrentes para que se generen solos.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={runRecurrent}
            data-testid="expenses-run-recurrent"
            className="ds-btn"
            title="Generar ahora los gastos recurrentes vencidos"
          >
            <RefreshCw size={14} /> Generar recurrentes
          </button>
          <button
            type="button"
            onClick={exportXlsx}
            data-testid="expenses-export"
            className="ds-btn"
          >
            <Download size={14} /> Excel
          </button>
          {canEdit && (
            <ExpenseBulkImportPanel onImported={load} />
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="expenses-new"
              className="ds-btn primary"
            >
              <Plus size={14} /> Nuevo gasto
            </button>
          )}
        </div>
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard label="Base del mes" value={formatEUR(summary.base_amount)} />
        <SummaryCard label="IVA soportado" value={formatEUR(summary.vat_amount)} />
        <SummaryCard label="Total gastos" value={formatEUR(summary.total)} bold />
        <SummaryCard label="Nº gastos del mes" value={summary.count} />
        <SummaryCard label="Recurrentes activos" value={summary.recurrent_active} />
      </div>

      {/* Filtros */}
      <div className="ds-card p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {PERIOD_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => pickPreset(p.id)}
              data-testid={`expenses-period-${p.id}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${
                periodId === p.id ? "bg-[#6E8B3D] text-white border-[#6E8B3D]" : "border-[#E5E5E0] hover:border-[#111110]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Desde</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriodId("custom"); }}
              data-testid="expenses-from"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Hasta</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriodId("custom"); }}
              data-testid="expenses-to"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Proveedor</label>
            <input value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}
              data-testid="expenses-filter-supplier"
              placeholder="Filtrar por proveedor..."
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Categoría</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="expenses-filter-category"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm">
              <option value="">Todas</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="ds-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F7F4] border-b border-[#E5E5E0] font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Proveedor</th>
              <th className="px-3 py-2 text-left">Descripción</th>
              <th className="px-3 py-2 text-right">Base</th>
              <th className="px-3 py-2 text-right">IVA</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-center">Rec.</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="expenses-table">
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-[#525252] text-xs">Cargando...</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-[#525252] text-xs" data-testid="expenses-empty">Sin gastos en este periodo.</td></tr>
            ) : list.map((e) => (
              <tr key={e.id} className="border-b border-[#E5E5E0] hover:bg-[#FAFAF7]">
                <td className="px-3 py-2 font-mono text-xs">{e.expense_date}</td>
                <td className="px-3 py-2">{e.supplier}</td>
                <td className="px-3 py-2 text-[#525252]">{e.description}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{formatEUR(e.base_amount)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{formatEUR(e.vat_amount)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right font-bold">{formatEUR(e.total)}</td>
                <td className="px-3 py-2 text-center">
                  {e.is_recurrent && <Repeat size={12} className="inline text-[#6E8B3D]" />}
                  {e.auto_generated && <span className="ml-1 text-[9px] font-mono uppercase text-amber-700">auto</span>}
                </td>
                <td className="px-3 py-2 text-xs">{STATUS_LABEL[e.status] || e.status}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  {canEdit && (
                    <button onClick={() => { setEditing(e); setShowForm(true); }} className="text-xs underline hover:text-[#6E8B3D]" data-testid={`expense-edit-${e.id}`}>Editar</button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(e.id)} className="text-xs underline hover:text-red-600" data-testid={`expense-delete-${e.id}`}>Eliminar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ExpenseForm
          initial={editing}
          categories={categories}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={onCreated}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, bold }) {
  return (
    <div className="ds-card p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className={`mt-1 font-mono ${bold ? "text-xl font-bold text-[#6E8B3D]" : "text-lg"}`}>{value}</div>
    </div>
  );
}

function ExpenseForm({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    supplier: initial?.supplier || initial?.supplier_name || "",
    supplier_id: initial?.supplier_id || "",
    supplier_tax_id: initial?.supplier_tax_id || "",
    description: initial?.description || "",
    expense_date: initial?.expense_date || todayISO(),
    total: initial?.total ?? "",
    vat_rate: initial?.vat_rate ?? 21,
    category: initial?.category || "",
    payment_method: initial?.payment_method || "",
    status: initial?.status || "pendiente",
    internal_notes: initial?.internal_notes || "",
    is_recurrent: initial?.is_recurrent || false,
    recurrence_frequency: initial?.recurrence_frequency || "monthly",
    recurrence_count: initial?.recurrence_count ?? "",
    recurrence_end: initial?.recurrence_end || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  // Calcular base/IVA aproximado para preview
  const preview = useMemo(() => {
    const t = Number(form.total) || 0;
    const vr = Number(form.vat_rate) || 0;
    const base = vr > 0 ? t / (1 + vr / 100) : t;
    return { base: base.toFixed(2), vat: (t - base).toFixed(2) };
  }, [form.total, form.vat_rate]);

  const save = async () => {
    if (!form.supplier.trim() || !form.description.trim()) {
      toast.error("Proveedor y descripción son obligatorios.");
      return;
    }
    if (form.total === "" || Number.isNaN(Number(form.total))) {
      toast.error("Indica el total del gasto.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        supplier: form.supplier.trim(),
        supplier_tax_id: (form.supplier_tax_id || "").trim(),
        description: form.description.trim(),
        expense_date: form.expense_date,
        total: Number(form.total),
        vat_rate: Number(form.vat_rate) || 0,
        category: form.category,
        payment_method: form.payment_method,
        status: form.status,
        internal_notes: form.internal_notes,
        is_recurrent: !!form.is_recurrent,
      };
      if (form.is_recurrent) {
        payload.recurrence_frequency = form.recurrence_frequency;
        payload.recurrence_end = form.recurrence_end || null;
        payload.recurrence_count = form.recurrence_count ? Number(form.recurrence_count) : null;
      }
      if (initial?.id) {
        await api.put(`/expenses/${initial.id}`, payload);
        toast.success("Gasto actualizado");
      } else {
        await api.post("/expenses", payload);
        toast.success("Gasto creado");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#111110] shadow-hard w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="expenses-form">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm">
            {initial ? "Editar gasto" : "Nuevo gasto"}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Proveedor *">
              <SupplierPicker
                value={{ id: form.supplier_id, name: form.supplier, tax_id: form.supplier_tax_id }}
                onChange={(next) => setForm((f) => ({
                  ...f,
                  supplier: next.name || "",
                  supplier_id: next.id || "",
                  supplier_tax_id: next.tax_id || "",
                }))}
                testid="expense-supplier"
              />
            </Field>
            <Field label="Fecha del gasto">
              <input type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)}
                data-testid="expense-date"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Descripción *">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              data-testid="expense-description" rows={2}
              className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Total € *">
              <input type="number" step="0.01" min="0" value={form.total} onChange={(e) => set("total", e.target.value)}
                data-testid="expense-total"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono text-right" />
            </Field>
            <Field label="IVA %">
              <input type="number" step="0.01" min="0" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)}
                data-testid="expense-vat-rate"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono text-right" />
            </Field>
            <div className="text-xs text-[#525252] flex flex-col justify-end">
              <div>Base imponible: <span className="font-mono">€{preview.base}</span></div>
              <div>Importe IVA: <span className="font-mono">€{preview.vat}</span></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Categoría">
              <select value={form.category} onChange={(e) => set("category", e.target.value)}
                data-testid="expense-category"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm">
                <option value="">— Sin categoría —</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Forma de pago">
              <input value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)}
                data-testid="expense-payment-method"
                placeholder="ej. Transferencia, Domiciliación..."
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
            </Field>
            <Field label="Estado">
              <select value={form.status} onChange={(e) => set("status", e.target.value)}
                data-testid="expense-status"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm">
                <option value="pendiente">Pendiente</option>
                <option value="pagado">Pagado</option>
                <option value="conciliado">Conciliado</option>
              </select>
            </Field>
          </div>
          <Field label="Nota interna">
            <textarea value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)}
              data-testid="expense-notes" rows={2}
              className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </Field>

          {/* Recurrencia */}
          <div className="border border-[#E5E5E0] p-3 bg-[#FAFAF7] space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="checkbox" checked={form.is_recurrent} onChange={(e) => set("is_recurrent", e.target.checked)}
                data-testid="expense-is-recurrent" />
              <Repeat size={14} /> Marcar como recurrente
            </label>
            {form.is_recurrent && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Frecuencia">
                  <select value={form.recurrence_frequency} onChange={(e) => set("recurrence_frequency", e.target.value)}
                    data-testid="expense-frequency"
                    className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm">
                    <option value="monthly">Mensual</option>
                    <option value="quarterly">Trimestral</option>
                    <option value="yearly">Anual</option>
                  </select>
                </Field>
                <Field label="Durante (meses, opcional)">
                  <input type="number" min="1" value={form.recurrence_count} onChange={(e) => set("recurrence_count", e.target.value)}
                    data-testid="expense-count"
                    placeholder="ej. 12"
                    className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
                </Field>
                <Field label="Hasta fecha (opcional)">
                  <input type="date" value={form.recurrence_end} onChange={(e) => set("recurrence_end", e.target.value)}
                    data-testid="expense-end"
                    className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
                </Field>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={onClose} className="flex-1 border border-[#111110] hover:bg-[#F7F7F4] px-3 py-2 text-sm" data-testid="expense-cancel">Cancelar</button>
            <button onClick={save} disabled={saving} className="flex-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1" data-testid="expense-save">
              <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
